import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GridLayout from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import DashboardHero from './cards/DashboardHero';
import ActivityCard from './cards/ActivityCard';
import EventsByNameCard from './cards/EventsByNameCard';
import RankHistoryCard from './cards/RankHistoryCard';
import HistoryCard from './cards/HistoryCard';
import TrainingsCard from './cards/TrainingsCard';
import GameloopTagsCard from './cards/GameloopTagsCard';
import RibbonsCard from './cards/RibbonsCard';
import MemberDashboardGridItem from './MemberDashboardGridItem';
import {
  DEFAULT_MEMBER_DASHBOARD_LAYOUT,
  MEMBER_DASHBOARD_DRAG_CANCEL,
  MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY,
  MEMBER_DASHBOARD_GRID_MARGIN,
  MEMBER_DASHBOARD_GRID_ROW_HEIGHT,
  memberDashboardLayoutsEqual,
  measureGameloopGridItemRows,
} from './memberDashboardLayoutConfig';
import {
  loadMemberDashboardLayout,
  saveMemberDashboardLayout,
  clearMemberDashboardLayout,
  getMemberDashboardLayoutStorageKey,
} from './memberDashboardLayoutStorage';
import MemberDashboardLayoutToolbar from './MemberDashboardLayoutToolbar';

function mergeVisibleIntoLayout(fullLayout, visibleLayout, { preserveGameloopHeight = false } = {}) {
  return fullLayout.map((item) => {
    const moved = visibleLayout.find((entry) => entry.i === item.i);
    if (!moved) return item;

    if (preserveGameloopHeight && item.i === MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY) {
      const { h: _ignoredHeight, ...restMoved } = moved;
      return { ...item, ...restMoved };
    }

    return { ...item, ...moved };
  });
}

export default function MemberDashboardGrid({
  userId,
  isViewAsActive = false,
  viewAsUserId = null,
  isLoggedIn,
  dashboardHandleId,
  dashboardClusterScope = false,
  player,
  loading,
  shellVisible = false,
  moniker,
  onDiscordAvatarError,
  onRingColorSaved,
}) {
  const layoutScope = useMemo(
    () => ({ userId, isViewAsActive, viewAsUserId }),
    [userId, isViewAsActive, viewAsUserId]
  );
  const viewerCacheKey = useMemo(
    () => {
      const uid = userId ?? 0;
      const vid = isViewAsActive ? (viewAsUserId ?? 0) : 0;
      return `u:${uid}:v:${vid}`;
    },
    [userId, isViewAsActive, viewAsUserId]
  );
  const layoutScopeKey = useMemo(
    () => getMemberDashboardLayoutStorageKey(layoutScope) ?? 'member-dashboard-default',
    [layoutScope]
  );
  const gridRef = useRef(null);
  const gameloopBodyRef = useRef(null);
  const initialPositionRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const skipPersistRef = useRef(true);
  const resetLayoutGuardRef = useRef(false);
  const [gridWidth, setGridWidth] = useState(1200);
  const [layout, setLayout] = useState(DEFAULT_MEMBER_DASHBOARD_LAYOUT);
  const [gridRemountKey, setGridRemountKey] = useState(0);
  const [gameloopEditing, setGameloopEditing] = useState(false);
  const [gameloopAutoH, setGameloopAutoH] = useState(null);
  const [hasCustomLayout, setHasCustomLayout] = useState(false);

  const showHandleCards = isLoggedIn && (shellVisible || dashboardHandleId != null);

  const visibleKeys = useMemo(() => {
    const keys = new Set(['hero']);
    if (showHandleCards) {
      keys.add('activity');
      keys.add('eventsByName');
      keys.add(MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY);
      keys.add('trainings');
      keys.add('rankHistory');
      keys.add('history');
      keys.add('ribbons');
    }
    return keys;
  }, [showHandleCards]);

  const visibleLayout = useMemo(
    () => layout.filter((item) => visibleKeys.has(item.i)),
    [layout, visibleKeys]
  );

  const displayLayout = useMemo(
    () =>
      visibleLayout.map((item) => {
        if (item.i !== MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY || gameloopAutoH == null) {
          return item;
        }

        const minH = item.minH ?? 6;
        return {
          ...item,
          h: Math.max(item.h, gameloopAutoH, minH),
        };
      }),
    [visibleLayout, gameloopAutoH]
  );

  const handleGameloopLayoutEditChange = useCallback(({ structureEditing, tagsEditing }) => {
    setGameloopEditing(Boolean(structureEditing || tagsEditing));
  }, []);

  useEffect(() => {
    if (!gridRef.current) return undefined;

    const observer = new ResizeObserver((entries) => {
      setGridWidth(entries[0].contentRect.width);
    });
    observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) return undefined;

    const handleMouseDown = (event) => {
      initialPositionRef.current = { x: event.clientX, y: event.clientY };
      isDraggingRef.current = false;
    };

    gridElement.addEventListener('mousedown', handleMouseDown);
    return () => gridElement.removeEventListener('mousedown', handleMouseDown);
  }, []);

  useEffect(() => {
    skipPersistRef.current = true;
    setGameloopEditing(false);
    setGameloopAutoH(null);

    if (!userId) {
      setLayout(DEFAULT_MEMBER_DASHBOARD_LAYOUT);
      setHasCustomLayout(false);
      skipPersistRef.current = false;
      return;
    }

    const saved = loadMemberDashboardLayout(layoutScope);
    setHasCustomLayout(saved != null);
    setLayout(saved ?? DEFAULT_MEMBER_DASHBOARD_LAYOUT);
    skipPersistRef.current = false;
  }, [layoutScopeKey, layoutScope, userId]);

  useEffect(() => {
    if (loading) {
      skipPersistRef.current = true;
      return;
    }
    skipPersistRef.current = false;
  }, [loading, showHandleCards]);

  useEffect(() => {
    if (!gameloopEditing) {
      setGameloopAutoH(null);
      return undefined;
    }

    const bodyEl = gameloopBodyRef.current;
    if (!bodyEl) return undefined;

    setGameloopAutoH((current) => {
      if (current != null) return current;
      const baseItem = layout.find((item) => item.i === MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY);
      return baseItem?.h ?? null;
    });

    let rafId = 0;
    let cancelled = false;

    const runMeasure = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = window.requestAnimationFrame(() => {
          if (cancelled) return;

          const nextH = measureGameloopGridItemRows(
            bodyEl,
            MEMBER_DASHBOARD_GRID_ROW_HEIGHT,
            MEMBER_DASHBOARD_GRID_MARGIN,
          );
          if (nextH == null) return;

          setGameloopAutoH((current) => (current === nextH ? current : nextH));
        });
      });
    };

    runMeasure();

    const observer = new ResizeObserver(runMeasure);
    observer.observe(bodyEl);

    const cardEl = bodyEl.querySelector('.member-dashboard__gameloop-card');
    if (cardEl) {
      observer.observe(cardEl);
    }

    const structureBody = bodyEl.querySelector('.member-dashboard__structure-block-body');
    if (structureBody) {
      observer.observe(structureBody);
    }

    const tagsBody = bodyEl.querySelector('.member-dashboard__gameloop-tags-body');
    if (tagsBody) {
      observer.observe(tagsBody);
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [gameloopEditing, layout, showHandleCards]);

  const persistLayout = useCallback(
    (updater, { preserveGameloopHeight = false } = {}) => {
      const shouldPersist =
        !skipPersistRef.current &&
        !resetLayoutGuardRef.current &&
        !gameloopEditing &&
        Boolean(userId);

      let nextLayoutForFlags = null;

      setLayout((current) => {
        let nextLayout = typeof updater === 'function' ? updater(current) : updater;

        if (preserveGameloopHeight && gameloopAutoH != null) {
          nextLayout = nextLayout.map((item) => {
            if (item.i !== MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY) return item;
            const base = current.find((entry) => entry.i === item.i);
            return base ? { ...item, h: base.h } : item;
          });
        }

        nextLayoutForFlags = nextLayout;

        if (shouldPersist && !memberDashboardLayoutsEqual(nextLayout)) {
          saveMemberDashboardLayout(layoutScope, nextLayout);
        }

        return nextLayout;
      });

      if (shouldPersist && nextLayoutForFlags) {
        setHasCustomLayout(!memberDashboardLayoutsEqual(nextLayoutForFlags));
      }
    },
    [gameloopAutoH, gameloopEditing, layoutScope, userId]
  );

  const handleResetLayout = useCallback(() => {
    skipPersistRef.current = true;
    resetLayoutGuardRef.current = true;
    clearMemberDashboardLayout(layoutScope);
    setLayout(DEFAULT_MEMBER_DASHBOARD_LAYOUT);
    setHasCustomLayout(false);
    setGameloopEditing(false);
    setGameloopAutoH(null);
    setGridRemountKey((current) => current + 1);

    window.setTimeout(() => {
      resetLayoutGuardRef.current = false;
      skipPersistRef.current = false;
    }, 150);
  }, [layoutScope]);

  const handleDrag = (_layout, _oldItem, _newItem, _placeholder, event) => {
    if (isDraggingRef.current || !event) return;

    const { x, y } = initialPositionRef.current;
    if (Math.hypot(event.clientX - x, event.clientY - y) > 50) {
      isDraggingRef.current = true;
    }
  };

  const handleDragStop = (nextVisibleLayout, oldItem, newItem) => {
    const preserveGameloopHeight = gameloopEditing;

    if (!isDraggingRef.current) {
      persistLayout(
        (current) =>
          mergeVisibleIntoLayout(current, nextVisibleLayout, { preserveGameloopHeight }).map(
            (item) => (item.i === newItem.i ? { ...oldItem } : item)
          ),
        { preserveGameloopHeight }
      );
      return;
    }

    persistLayout(
      (current) => mergeVisibleIntoLayout(current, nextVisibleLayout, { preserveGameloopHeight }),
      { preserveGameloopHeight }
    );
  };

  const handleLayoutChange = (nextVisibleLayout) => {
    if (skipPersistRef.current || resetLayoutGuardRef.current || gameloopEditing) return;

    persistLayout((current) => mergeVisibleIntoLayout(current, nextVisibleLayout));
  };

  return (
    <div className="member-dashboard__layout-root">
      {isLoggedIn && userId ? (
        <MemberDashboardLayoutToolbar
          showReset={hasCustomLayout}
          onReset={handleResetLayout}
        />
      ) : null}

      <div ref={gridRef} className="member-dashboard__grid">
      <GridLayout
        key={`${layoutScopeKey}-${gridRemountKey}`}
        className="member-dashboard__grid-layout"
        layout={displayLayout}
        cols={12}
        rowHeight={MEMBER_DASHBOARD_GRID_ROW_HEIGHT}
        width={gridWidth || window.innerWidth || 1200}
        margin={MEMBER_DASHBOARD_GRID_MARGIN}
        containerPadding={[0, 0]}
        isResizable={true}
        isDraggable={true}
        onDrag={handleDrag}
        onDragStop={handleDragStop}
        onLayoutChange={handleLayoutChange}
        compactType="vertical"
        dragStartThreshold={50}
        preventCollision={false}
        draggableCancel={MEMBER_DASHBOARD_DRAG_CANCEL}
      >
        <MemberDashboardGridItem
          key="hero"
          content={
            <DashboardHero
              player={player}
              loading={loading}
              moniker={moniker}
              handleId={dashboardHandleId}
              onDiscordAvatarError={onDiscordAvatarError}
              onRingColorSaved={onRingColorSaved}
            />
          }
        />

        {showHandleCards ? (
          <MemberDashboardGridItem
            key="activity"
            content={
              <ActivityCard
                player={player}
                handleId={dashboardHandleId}
                loading={loading}
              />
            }
          />
        ) : null}

        {showHandleCards ? (
          <MemberDashboardGridItem
            key="eventsByName"
            content={
              <EventsByNameCard
                player={player}
                handleId={dashboardHandleId}
                loading={loading}
              />
            }
          />
        ) : null}

        {showHandleCards ? (
          <MemberDashboardGridItem
            key={MEMBER_DASHBOARD_GAMELOOP_ITEM_KEY}
            className={gameloopEditing ? 'member-dashboard__grid-item--expanded' : undefined}
            bodyRef={gameloopBodyRef}
            bodyClassName={
              gameloopEditing ? 'member-dashboard__grid-item-body--expanded' : undefined
            }
            content={
              <GameloopTagsCard
                handleId={dashboardHandleId}
                player={player}
                loading={loading}
                viewerCacheKey={viewerCacheKey}
                onLayoutEditChange={handleGameloopLayoutEditChange}
              />
            }
          />
        ) : null}

        {showHandleCards ? (
          <MemberDashboardGridItem
            key="trainings"
            content={
              <TrainingsCard
                player={player}
                loading={loading}
                gridColSpan={displayLayout.find((item) => item.i === 'trainings')?.w ?? 6}
              />
            }
          />
        ) : null}

        {showHandleCards ? (
          <MemberDashboardGridItem
            key="rankHistory"
            content={
              <RankHistoryCard
                handleId={dashboardHandleId}
                player={player}
                loading={loading}
                viewerCacheKey={viewerCacheKey}
              />
            }
          />
        ) : null}

        {showHandleCards ? (
          <MemberDashboardGridItem
            key="history"
            content={
              <HistoryCard
                handleId={dashboardHandleId}
                player={player}
                loading={loading}
                viewerCacheKey={viewerCacheKey}
              />
            }
          />
        ) : null}

        {showHandleCards ? (
          <MemberDashboardGridItem
            key="ribbons"
            content={
              <RibbonsCard
                handleId={dashboardHandleId}
                player={player}
                loading={loading}
                dashboardClusterScope={dashboardClusterScope}
              />
            }
          />
        ) : null}
      </GridLayout>
    </div>
    </div>
  );
}
